/**
 * 阿里云验证码 2.0 前端接入（2026-09-18，V2 架构官方文档 + 官方 React Demo 接线）。
 *
 * 官方要点（help.aliyun.com/zh/captcha/web-and-h5-client-v2-architecture-access）：
 * - SDK 必须动态引入（o.alicdn.com/AliyunCaptcha.js），禁止拉到本地部署——会无法更新安全能力。
 * - initAliyunCaptcha 参数：SceneId（场景 ID）、prefix（身份标，控制台概览页）、mode: 'popup'、
 *   element（渲染容器）、button（触发元素选择器，SDK 在其上挂 click 监听弹窗）、
 *   captchaVerifyCallback（滑块通过后回调，返回 { captchaResult, bizResult? }）、getInstance、
 *   language（cn/tw/en）、slideStyle（width ≥ 320）。
 * - captchaVerifyCallback 收到 captchaVerifyParam（票据），原样传给服务端即可，禁止修改。
 * - 初始化加载尽量前置（与验证请求间隔 >2s，利于资源预载）——信使页 mount 即 init。
 * - React Demo 卸载清理：remove #aliyunCaptcha-mask / #aliyunCaptcha-window-popup，
 *   否则重复 mount 会多次回调 captchaVerifyCallback。
 *
 * 本地 dev：VITE_CAPTCHA_DISABLE=true 或凭据缺失 → 旁路模式（直接提交空票据，
 * 服务端 CAPTCHA_VERIFY_DISABLED=true 跳过验签）。staging/prod SDK 加载失败 →
 * 同样空票据提交，由服务端 fail-closed 拒绝（401 提示重试）。
 * 凭据来源：构建时注入 VITE_CAPTCHA_REGION / VITE_CAPTCHA_PREFIX / VITE_CAPTCHA_SCENE_ID
 * （均为公开值，非密钥，红线 4 不适用）。
 */

export interface CaptchaCreds {
  region: 'cn' | 'sgp';
  prefix: string;
  sceneId: string;
}

/** 读构建时注入的公开凭据；未配置（本地 dev）→ null（旁路模式） */
export function captchaCreds(): CaptchaCreds | null {
  if (import.meta.env.VITE_CAPTCHA_DISABLE === 'true') return null;
  const region = import.meta.env.VITE_CAPTCHA_REGION as 'cn' | 'sgp' | undefined;
  const prefix = import.meta.env.VITE_CAPTCHA_PREFIX as string | undefined;
  const sceneId = import.meta.env.VITE_CAPTCHA_SCENE_ID as string | undefined;
  if (!region || !prefix || !sceneId) return null;
  return { region, prefix, sceneId };
}

/* ---------------- 类型声明（官方 SDK 无官方 .d.ts，按文档 + Demo 定义最小面） ---------------- */

interface InitParams {
  SceneId: string;
  prefix: string;
  mode: 'popup' | 'embed';
  element: string;
  button: string;
  captchaVerifyCallback: (
    captchaVerifyParam: string,
  ) => Promise<{ captchaResult: boolean; bizResult?: boolean }>;
  onBizResultCallback: (bizResult: boolean) => void;
  getInstance: (instance: CaptchaInstance) => void;
  slideStyle: { width: number; height: number };
  language: 'cn' | 'tw' | 'en';
  onError?: (e: unknown) => void;
}

interface CaptchaInstance {
  refresh?: () => void; // 官方常见问题 Q4：autoRefresh=false 或异常恢复时手动刷新
  destroy?: () => void;
}

declare global {
  interface Window {
    /** 官方要求：region + prefix 必须经此全局变量传（SDK 加载前定义）。region 默认 cn——
     * 场景在新加坡区（sgp）时若不设置，客户端行为数据回传中国内地管控中心，与场景区域不一致 → 验证链路异常 */
    AliyunCaptchaConfig?: { region: 'cn' | 'sgp'; prefix: string };
    initAliyunCaptcha?: (params: InitParams) => void;
  }
}

const SDK_URL = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';

/* ---------------- 单例加载器（StrictMode 双跑 / 多次 effect 复用同一次加载与 init） ---------------- */

let loadPromise: Promise<void> | null = null;
let inited = false;

function loadSdk(creds: CaptchaCreds): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (window.initAliyunCaptcha) return resolve();
    // ① 全局配置（region + prefix）必须在 SDK 脚本加载前定义（官方文档接入步骤 1）
    window.AliyunCaptchaConfig = { region: creds.region, prefix: creds.prefix };
    // ② 动态引入主 JS（官方禁止本地部署）
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null; // 允许下次重试
      reject(new Error('captcha-sdk-load-failed'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** React 卸载清理（官方 Demo 要求：防重复 mount 导致 captchaVerifyCallback 多次回调） */
export function cleanupCaptchaDom(): void {
  document.getElementById('aliyunCaptcha-mask')?.remove();
  document.getElementById('aliyunCaptcha-window-popup')?.remove();
}

export interface UseCaptchaOptions {
  /** element 容器 DOM id（常驻渲染；视觉藏起但不能 display:none——SDK 可能测尺寸） */
  elementId: string;
  /** 触发按钮 DOM id（SDK 在其上挂 click 弹窗；常驻隐藏按钮，封缄按钮程序化 click() 触发） */
  triggerId: string;
  language: 'cn' | 'en';
  /**
   * 滑块通过后的业务提交（captchaVerifyParam 原样透传给服务端）。
   * 返回是否成功：成功 true；失败 false（SDK 重置滑块，页面自身已展示错误态）。
   */
  onVerify: (captchaVerifyParam: string) => Promise<boolean>;
}

/**
 * SDK JS 预加载（mount 时调用）：官方要求「加载尽量前置（与验证请求间隔 >2s，利于资源预载）」。
 * 只拉 JS 不 init——initAliyunCaptcha 需要触发按钮已在 DOM 且可测量（mount 时封缄按钮还没进
 * confirm 区，hidden 祖先内不可测量 → 绑定失效，真机实测滑块不弹）。
 */
export function preloadCaptchaSdk(): void {
  const creds = captchaCreds();
  if (!creds) return; // 旁路模式
  void loadSdk(creds).catch(() => {
    captchaBroken = true; // 预载失败 → 旁路（页面自行 sendLetter('')，服务端 fail-closed 兜底）
  });
}

/**
 * 初始化验证码并接管触发按钮点击（在 preview 态渲染稳定后调用一次；StrictMode 双跑安全）。
 * 页面侧交互契约：用户真实点击封缄按钮（trusted event）→ SDK 接管（弹挑战框/后台验证）→
 * 通过 → captchaVerifyCallback。旁路模式：页面自行 sendLetter('')。
 *
 * ⚠️ init 时机（2026-09-21 定稿，推翻 9/19「惰性 init」）：init 必须发生在 **preview 渲染稳定后、
 * 用户点击前**——「点击瞬间 init」会在同一事件循环里叠加 React setPhase('sending') 渲染，
 * 与 SDK 初始化互相踩踏 → 主线程同步卡死（生产 9/20-21 用户实测「页面无响应」，CDP 复现；
 * 同一页面用独立按钮 init 则完全正常，差异锁死在 React 状态竞态）。9/19「preview init 绑定
 * 不生效」的结论是在场景形态配置漂移（TRACELESS/F004）时段得出，不可靠。
 * 双保险：preview effect 调 initCaptcha；封缄点击时 sealClickInit 幂等兜底（若 init 因故未跑）。
 */
export function initCaptcha(opts: UseCaptchaOptions): void {
  const creds = captchaCreds();
  if (!creds) return; // 旁路模式：页面自行处理
  if (inited) return; // 官方：不支持重复 init（语言切换场景后续优化，见实现说明）
  inited = true; // 立即占位——init 是异步链，StrictMode 双跑时第二个调用必须在这里被挡住
  // （否则两次 loadSdk.then 都执行 → window.initAliyunCaptcha 被调用两次 → SDK 内部状态
  //   指向被 cleanup 删除的 DOM，弹窗「创建但永不显示」，真机实测复现）

  void loadSdk(creds)
    .then(async () => {
      if (!window.initAliyunCaptcha) throw new Error('captcha-sdk-missing');
      // 等触发按钮真实出现在 DOM（preview effect 与按钮 mount 同一 commit，但 SDK init 内部
      // 异步测量时机更晚——真机实测 init 时查询不到按钮 → SDK 绑定空占位 handler，弹窗永不显示）
      for (let i = 0; i < 100; i++) {
        if (document.getElementById(opts.triggerId)) break;
        await new Promise((r) => setTimeout(r, 100));
      }
      // 再等两帧：确保按钮所在的 preview 渲染已提交、无进行中的 React 状态变更
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      // 关键（2026-09-19 深夜定稿）：React 19 创建 button 时写 onclick=noop（bundle 内 $n），
      // SDK 绑定时见非空 onclick 会跳过绑定（伪代码 n.onclick ??= r）→ 恒 noop → 点击无反应。
      // init 前显式清空，SDK 才会真正挂上 handler：
      const sealBtn = document.getElementById(opts.triggerId);
      if (sealBtn) sealBtn.onclick = null;
      window.initAliyunCaptcha({
        SceneId: creds.sceneId,
        prefix: creds.prefix,
        mode: 'popup',
        element: `#${opts.elementId}`,
        button: `#${opts.triggerId}`,
        captchaVerifyCallback: async (captchaVerifyParam) => {
          const ok = await opts.onVerify(captchaVerifyParam);
          // captchaResult：验证码侧结果（滑块已过，服务端验签失败也归入 biz 侧由页面错误态呈现）
          // bizResult：业务提交结果——false 时 SDK 重置滑块允许重试
          return { captchaResult: true, bizResult: ok };
        },
        onBizResultCallback: () => {
          /* 业务结果已由调用侧（sendLetter 三态机）处理状态与文案 */
        },
        getInstance: (instance) => {
          captchaInstance = instance;
        },
        slideStyle: { width: 320, height: 40 },
        language: opts.language === 'cn' ? 'cn' : 'en',
        onError: () => {
          // 初始化请求失败/超时（控制台/网络层）：验证码不可用 → 旁路提交，
          // 服务端 fail-closed 拒绝空票据（401「人机验证未通过」提示重试）
          captchaBroken = true;
        },
      });
      // 绑定校验（2026-09-19 深夜定稿）：SDK 绑 button 用 onclick 属性。React 19 创建 button 时
      // 会先写 onclick=noop（bundle 内 function $n(){}）——若 init 后 onclick 仍为 noop 说明
      // SDK 绑定未生效（内部状态竞争），置 inited=false 允许 sealAndSend 下次点击重试 init。
      await new Promise((r) => setTimeout(r, 1200));
      const btn = document.getElementById(opts.triggerId);
      if (btn && String(btn.onclick).includes('$n') === false) {
        bound = true; // native handler 已挂
      } else {
        inited = false; // 未绑定成功——下次点击重试
      }
    })
    .catch(() => {
      captchaBroken = true;
      inited = false; // 失败允许重试
    });
}

// getInstance 绑定（官方固定写法）：实例方法（refresh/destroy）后续按需扩展
let captchaInstance: CaptchaInstance | null = null;
void captchaInstance;
let captchaBroken = false;
let bound = false; // onclick 已被 SDK 覆盖为真实 handler（非 React noop）
let popupFixInstalled = false; // installPopupShowFix 已安装（showFix 只装一次）

/**
 * 弹窗显示接管（2026-09-19 深夜，SDK 3.29.0 + CHECK_BOX 形态 workaround）：
 * SDK 点击后创建 popup DOM 但显示类不解除（显示逻辑依赖复选框验证请求返回，
 * 而验证请求又等弹窗内交互——显示死锁，真机实测复现）。此处监视 popup 创建，出现后强制移除
 * 隐藏类让弹窗可见（内容本身渲染完整、功能正常——移除后复选框可点、验证可完成）。
 * 显隐类两套都在（外壳 aliyunCaptcha-hidden / 内层 window-hidden + mask-hidden），一并处理。
 * MutationObserver 挂 body（popup 由 SDK 动态 append）。
 */
export function installPopupShowFix(triggerId: string): void {
  if (!captchaCreds()) return; // 旁路模式不装
  if (popupFixInstalled) return; // 幂等（模块级标志——封缄按钮常驻 DOM，不能用 getElementById 判断）
  popupFixInstalled = true;
  const show = (el: HTMLElement | null) => {
    el?.classList.remove('aliyunCaptcha-hidden', 'window-hidden', 'mask-hidden');
    el?.style.removeProperty('display');
  };
  // 只在用户点击封缄之后才掀开弹窗——SDK init 时会**预创建**隐藏 popup（CHECK_BOX 形态），
  // 无条件掀开会导致「还没点封缄弹窗就自己弹出来」（真机实测）。
  // 监听挂 document 捕获段按 id 过滤（封缄按钮 preview 态才进 DOM——mount 时直接查会落空）。
  let armed = false;
  const onCaptureClick = (e: Event) => {
    const t = e.target as HTMLElement | null;
    if (t?.closest?.(`#${triggerId}`)) armed = true;
  };
  document.addEventListener('click', onCaptureClick, { capture: true });
  // 掀弹窗 = 确定性执行：点击后短时间内多次尝试（SDK 创建 popup 的时机在 click 之后异步，
  // 单次 observer 可能赶不上或被内部状态覆盖）。上限 15 次 × 400ms = 6s，弹窗出现即停。
  const tryShow = () => {
    if (!armed) return false;
    const popup = document.getElementById('aliyunCaptcha-window-popup');
    if (!popup) return false; // 还没创建，继续等（此前误返回 true → interval 提前停止 → 之后创建的 popup 没人掀）
    show(popup);
    show(document.getElementById('aliyunCaptcha-mask'));
    return popup.classList.contains('aliyunCaptcha-hidden') === false;
  };
  // popup 可能在 init 时已预创建（点击前就挂在 body）——点击后 SDK 只改其 class/display。
  // ⚠️ 监听只用 childList（popup append 事件）：attributes+subtree 会让「我们 remove class ↔
  // SDK 加回 class」互相触发 observer → 与 SDK 内部状态机互搏 → 主线程卡死（9/21 用户实测页面
  // 无响应 + CDP 协议超时，时间线与该改动完全吻合）。预创建场景由下方 interval 兜底。
  const observer = new MutationObserver(() => { tryShow(); });
  observer.observe(document.body, { childList: true, subtree: false });
  document.addEventListener('click', () => {
    if (!armed) return;
    let n = 0;
    const timer = window.setInterval(() => {
      n += 1;
      const done = tryShow();
      // 60 次 × 400ms = 24s：二次挑战的验证请求慢（风控往返），15 次会提前放弃导致「永远卡在路上」
      if (done || n >= 60) window.clearInterval(timer);
    }, 400);
  }, { capture: true });
}

/** SDK 完成初始化且 button 绑定生效——页面据此决定让位给 SDK 还是提示重试 */
export function captchaReady(): boolean {
  return inited && bound && !captchaBroken;
}

/**
 * 封缄点击兜底（2026-09-21 定稿）：init 已在 preview effect 提前跑（见 initCaptcha 注释——
 * 点击瞬间 init 会与 React 渲染竞态卡死主线程）。本函数只做幂等兜底：若 preview effect 的
 * init 因故未跑/未完成，这里补发（正常情况下 inited=true 直接返回 true）。
 * 返回 true = SDK 已接管（验证回调将异步到达）；false = 调用侧走旁路。
 */
export function sealClickInit(opts: UseCaptchaOptions): boolean {
  const creds = captchaCreds();
  if (!creds) return false; // 旁路
  initCaptcha({ ...opts, language: opts.language }); // 幂等：inited 后内部直接 return
  return captchaReady() || inited; // init 已启动即算接管（回调异步到达）
}
